/**
 * #668 — Optimistic volume slider for degraded transports.
 *
 * Pure coalescing/suppression logic, platform-agnostic (local zones and
 * network renderers alike). TransportCommand owns the singleton and supplies
 * the actual send/store/resync side effects via deps.
 *
 * Contract:
 * - UI applies every input optimistically (applyOptimistic) — never waits
 *   for the round trip.
 * - Sends are coalesced to at most one per VOLUME_MIN_SEND_INTERVAL_MS
 *   (~8/sec) during a drag; the latest value always wins; release always
 *   dispatches one final send regardless of the rate cap.
 * - Volume read-back from polls is suppressed while dragging and for
 *   VOLUME_READBACK_SUPPRESS_MS after release, then resyncOnce() fires a
 *   single reconciliation against the device.
 * - If a send fails: pending sends and the resync are cancelled, suppression
 *   is lifted, and onSendFailure receives the last accepted level so the UI
 *   can snap back and surface the error plainly.
 */

import { useMaestroStore } from '../store/maestroStore';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { showImperativeToast } from '../providers/UIProvider';
import { recordUserVolumeLevel, resolvePostDragVolumeReadback } from './volumeSelectReadback';

export const VOLUME_MIN_SEND_INTERVAL_MS = 125; // ~8 sends/sec
export const VOLUME_READBACK_SUPPRESS_MS = 1500;

export interface VolumeCoalescerDeps {
  /** Dispatch the level to the active output. Must reject on failure. */
  send(level: number): Promise<void>;
  /** Immediately reflect the level in UI state (store). */
  applyOptimistic(level: number): void;
  /** A send failed: snap back to snapBackLevel (null = unknown) and tell the user. */
  onSendFailure(snapBackLevel: number | null, err: unknown): void;
  /** One-shot device resync after the post-release suppression window. */
  resyncOnce(): void;
  /** Injectable clock/timers for tests. */
  now?(): number;
  schedule?(fn: () => void, ms: number): unknown;
  cancel?(handle: unknown): void;
}

export class VolumeCoalescer {
  private readonly _deps: VolumeCoalescerDeps;
  private readonly _now: () => number;
  private readonly _schedule: (fn: () => void, ms: number) => unknown;
  private readonly _cancel: (handle: unknown) => void;

  private _dragging = false;
  private _lastSendAt = Number.NEGATIVE_INFINITY;
  private _pendingLevel: number | null = null;
  private _pendingTimer: unknown = null;
  private _suppressUntil = 0;
  private _resyncTimer: unknown = null;
  /** Last level the device accepted (successful send or confirmed read-back). */
  private _lastAcceptedLevel: number | null = null;

  constructor(deps: VolumeCoalescerDeps) {
    this._deps = deps;
    this._now = deps.now ?? (() => Date.now());
    this._schedule = deps.schedule ?? ((fn, ms) => setTimeout(fn, ms));
    this._cancel = deps.cancel ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  }

  /** Drag started. currentLevel = last known good level, the snap-back target if nothing sends successfully. */
  beginDrag(currentLevel: number | null): void {
    this._dragging = true;
    this._suppressUntil = 0;
    this._cancelResync();
    if (typeof currentLevel === 'number' && Number.isFinite(currentLevel)) {
      this._lastAcceptedLevel = currentLevel;
    }
  }

  /** Slider moved. Optimistic UI now; coalesced send (latest value wins). */
  input(level: number): void {
    this._deps.applyOptimistic(level);
    const elapsed = this._now() - this._lastSendAt;
    if (elapsed >= VOLUME_MIN_SEND_INTERVAL_MS) {
      this._clearPending();
      this._dispatch(level);
      return;
    }
    this._pendingLevel = level;
    if (this._pendingTimer == null) {
      this._pendingTimer = this._schedule(() => {
        this._pendingTimer = null;
        const pending = this._pendingLevel;
        this._pendingLevel = null;
        if (pending != null) this._dispatch(pending);
      }, VOLUME_MIN_SEND_INTERVAL_MS - elapsed);
    }
  }

  /** Drag released. Always one final send; start the read-back suppression window. */
  release(level: number): void {
    this._deps.applyOptimistic(level);
    this._clearPending();
    this._dragging = false;
    this._dispatch(level);
    this._suppressUntil = this._now() + VOLUME_READBACK_SUPPRESS_MS;
    this._cancelResync();
    this._resyncTimer = this._schedule(() => {
      this._resyncTimer = null;
      this._deps.resyncOnce();
    }, VOLUME_READBACK_SUPPRESS_MS);
  }

  /** True while device volume read-back (polls/events) must be ignored. */
  isReadBackSuppressed(): boolean {
    return this._dragging || this._now() < this._suppressUntil;
  }

  /** Device-confirmed level (accepted read-back / resync result). */
  noteDeviceConfirmed(level: number): void {
    this._lastAcceptedLevel = level;
  }

  getLastAcceptedLevel(): number | null {
    return this._lastAcceptedLevel;
  }

  private _dispatch(level: number): void {
    this._lastSendAt = this._now();
    void this._deps
      .send(level)
      .then(() => {
        this._lastAcceptedLevel = level;
      })
      .catch((err) => this._handleSendFailure(err));
  }

  private _handleSendFailure(err: unknown): void {
    this._clearPending();
    this._cancelResync();
    this._dragging = false;
    this._suppressUntil = 0;
    this._deps.onSendFailure(this._lastAcceptedLevel, err);
  }

  private _clearPending(): void {
    if (this._pendingTimer != null) {
      this._cancel(this._pendingTimer);
      this._pendingTimer = null;
    }
    this._pendingLevel = null;
  }

  private _cancelResync(): void {
    if (this._resyncTimer != null) {
      this._cancel(this._resyncTimer);
      this._resyncTimer = null;
    }
  }
}

// ── VolumeCommandRouter — orchestration extracted from TransportCommand ────

export interface VolumeRouterDeps {
  /** TransportCommand._hydrateConnectorRendererFromActiveZone('volume'). */
  hydrate(): boolean;
  getAudioRef(): HTMLAudioElement | null;
  isTauri(): boolean;
  isIOS(): boolean;
  iosPostMessage(action: string, data: Record<string, any>): void;
  getConnector(): Promise<typeof import('../lib/connector')>;
}

const VOLUME_ECHO_WINDOW_MS = 3000;

/**
 * Owns volume dispatch, optimistic store writes, echo/read-back suppression,
 * failure snap-back, and the post-drag resync. TransportCommand delegates here.
 */
export class VolumeCommandRouter {
  private _deps: VolumeRouterDeps;
  private _volumeEcho: { target: number; timestamp: number } | null = null;
  private _coalescer: VolumeCoalescer;

  constructor(deps: VolumeRouterDeps) {
    this._deps = deps;
    this._coalescer = new VolumeCoalescer({
      send: (level) => this._sendCoalesced(level),
      applyOptimistic: (level) => this._applyOptimistic(level),
      onSendFailure: (snapBackLevel, err) => this._handleSendFailure(snapBackLevel, err),
      resyncOnce: () => { void this._resyncAfterDrag(); },
    });
  }

  /** One-shot volume set (keyboard shortcuts, Prism). Optimistic + immediate send + snap-back on failure. */
  async volume(level: number): Promise<void> {
    if (!this._deps.hydrate()) return;
    const store = useMaestroStore.getState();
    if (!store.activeConnectorRenderer && !this._deps.getAudioRef()) return;

    const prior = typeof store.volume === 'number' ? store.volume : null;
    this._applyOptimistic(level);
    try {
      await this._dispatch(level);
      this._coalescer.noteDeviceConfirmed(level);
    } catch (err) {
      this._handleSendFailure(prior, err);
    }
  }

  dragStart(): void {
    this._coalescer.beginDrag(useMaestroStore.getState().volume);
  }

  dragInput(level: number): void {
    this._coalescer.input(level);
  }

  dragRelease(level: number): void {
    this._coalescer.release(level);
  }

  /** True when a device-reported volume must be ignored (drag/post-release window or stale echo). */
  isVolumeEchoSuppressed(reportedLevel: number): boolean {
    if (this._coalescer.isReadBackSuppressed()) return true;
    if (!this._volumeEcho) return false;
    const elapsed = Date.now() - this._volumeEcho.timestamp;
    if (elapsed > VOLUME_ECHO_WINDOW_MS) {
      this._volumeEcho = null;
      return false;
    }
    return reportedLevel !== this._volumeEcho.target;
  }

  private _applyOptimistic(level: number): void {
    this._volumeEcho = { target: level, timestamp: Date.now() };
    useMaestroStore.getState().setVolume(level);
  }

  private async _sendCoalesced(level: number): Promise<void> {
    if (!this._deps.hydrate()) return;
    const store = useMaestroStore.getState();
    if (!store.activeConnectorRenderer && !this._deps.getAudioRef()) return;
    await this._dispatch(level);
  }

  /** Platform dispatch only — no store writes. Rejects on transport failure. */
  private async _dispatch(level: number): Promise<void> {
    const store = useMaestroStore.getState();
    const renderer = store.activeConnectorRenderer;
    const az = store.discovery?.activeZone as any;
    diagnosticLog(
      'info',
      'volume',
      `Volume command: ${Math.round(level)} to ${renderer?.name || az?.name || 'local'}`,
      {
        level: Math.round(level),
        renderer: renderer?.name,
        rendererType: /system (output|audio)/i.test(String(az?.name || '')) ? 'browser' : (az?.transport_type || az?.type),
        muted: !!store.muted,
      },
    );

    const audioRef = this._deps.getAudioRef();
    if (renderer && this._deps.isTauri()) {
      const C = await this._deps.getConnector();
      await C.setVolume(renderer.id, Math.round(level));
    } else if (renderer && this._deps.isIOS()) {
      this._deps.iosPostMessage('setVolume', {
        deviceId: renderer.id,
        level: Math.round(level),
      });
    } else if (audioRef) {
      audioRef.volume = level / 100;
    }
    if (renderer) recordUserVolumeLevel(renderer.id, level);
  }

  private _handleSendFailure(snapBackLevel: number | null, err: unknown): void {
    if (typeof snapBackLevel === 'number') {
      this._volumeEcho = { target: snapBackLevel, timestamp: Date.now() };
      useMaestroStore.getState().setVolume(snapBackLevel);
    }
    const reason = err instanceof Error ? err.message : String(err ?? 'unknown');
    diagnosticLog('warn', 'volume', `Volume send failed — snapped back to ${snapBackLevel ?? 'unknown'}`, {
      snapBackLevel,
      reason,
    });
    showImperativeToast(
      typeof snapBackLevel === 'number'
        ? `Volume change failed — reset to ${Math.round(snapBackLevel)}.`
        : 'Volume change failed — the device didn\u2019t respond.',
      5000,
    );
  }

  /** One-shot device resync 1.5s after drag release (skipped if a new drag started). */
  private async _resyncAfterDrag(): Promise<void> {
    const store = useMaestroStore.getState();
    const renderer = store.activeConnectorRenderer;
    if (!renderer) return; // local zone: the audio element is the source of truth
    if (this._coalescer.isReadBackSuppressed()) return; // new drag in progress
    // Let genuine device state through — the drag is settled.
    this._volumeEcho = null;
    if (this._deps.isTauri()) {
      try {
        const C = await this._deps.getConnector();
        const vol = await C.getVolumeBackground(renderer.id);
        if (this._coalescer.isReadBackSuppressed()) return;
        const resolved = resolvePostDragVolumeReadback(
          renderer.id,
          vol,
          this._coalescer.getLastAcceptedLevel(),
        );
        if (resolved == null) {
          const lastDragged = this._coalescer.getLastAcceptedLevel();
          if (typeof lastDragged === 'number') {
            this._volumeEcho = { target: lastDragged, timestamp: Date.now() };
          }
          return;
        }
        this._volumeEcho = { target: resolved, timestamp: Date.now() };
        this._coalescer.noteDeviceConfirmed(resolved);
        store.setVolume(resolved);
        diagnosticLog('info', 'volume', `Post-drag volume resync: ${resolved}`, { rendererId: renderer.id });
      } catch {
        // Read-back is no longer suppressed — the regular poll will reconcile.
      }
    } else if (this._deps.isIOS()) {
      try {
        (window as any).__maestroNative?.getVolume?.(renderer.id);
      } catch {}
    }
  }
}
